import { Avatar, Button, Flex, Select, Typography } from "antd";
import { AvailabilitySlots } from "components/AvailabilitySlots";
import { StyledNotification } from "components/AvailabilitySlots/styles";
import { Loader } from "components/Loader";
import { StartContent } from "components/StartContent";
import { SummaryTitle } from "components/StartContent/styled";
import { RESERVATION_OPTIONS, TRACKING } from "config/consts";
import { API_URL } from "config/api";
import { useApp } from "contexts/AppProvider";
import dayjs from "dayjs";
import { LOCALES } from "i18n/messages";
import { MAX_AVAILABILITY_MONTH_RETRIES, useGetAvailabilityQuery } from "queries/reservation.query";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { getAvailabilityParam } from "repositories/reservations/reservationRepository.param";
import { ISingleAvailability } from "types";
import { uniqueOptionIds } from "lib/optionIds";
import { ContinueTransaction } from "./ContinueTransaction";

type ISingleTherapist = { id: string; name: string };

export const Start = () => {
  const location = useLocation();
  const [availabilityPayload, setAvailabilityPayload] = useState<getAvailabilityParam | null>(null);
  const {
    branches,
    setReservationPayload,
    dispatchReservationPayload,
    reservationPayload,
    services,
    customerId,
    activeTimeslot,
    setActiveTimeslot,
    price,
    getBranchById,
    activeService,
    setActiveService,
    setActiveStep,
    sentEvents,
    currency,
  } = useApp();
  const [isChild, setIsChild] = useState<boolean>(false);
  const [birthYear, setBirthYear] = useState<string | null>(reservationPayload.birthYear ?? null);
  const [activeCity, setActiveCity] = useState<string | null>(reservationPayload.isOnline ? "online" : null);
  const [readyToFetch, setReadyToFetch] = useState<boolean>(false);
  const navigate = useNavigate();

  const [timeSchedules, setTimeSchedules] = useState<ISingleAvailability[]>([]);
  const [showSelect, setShowSelect] = useState<boolean>(false);
  const [loadedDays, setLoadedDays] = useState<number>(30);
  const [availabilityAnchorDate, setAvailabilityAnchorDate] = useState<string | null>(null);
  const [therapists, setTherapists] = useState<ISingleTherapist[]>([]);
  const [accumulatedAvailability, setAccumulatedAvailability] = useState<ISingleAvailability[]>([]);
  const [retries, setRetries] = useState<number>(0);

  const { locale } = useIntl();
  const { formatMessage } = useIntl();

  // Start is unmounted while the patient form is displayed. Restore its local
  // UI state from the reservation payload when the user comes back.
  useEffect(() => {
    if (!reservationPayload.serviceId || !services?.length) return;

    const persistedService = services.find((service) => service.id === reservationPayload.serviceId);
    if (!persistedService) return;

    if (activeService?.id !== persistedService.id) {
      setActiveService(persistedService);
    }

    const persistedVisitType = persistedService.options.find((option) => option.id === reservationPayload.visitTypeId);
    const child = ["Dziecko", "Dziecko / nastolatek"].includes(persistedVisitType?.name ?? "");
    setIsChild(child);
    setBirthYear(child ? (reservationPayload.birthYear ?? null) : null);
  }, [
    services,
    activeService?.id,
    reservationPayload.serviceId,
    reservationPayload.visitTypeId,
    reservationPayload.birthYear,
    setActiveService,
  ]);

  useEffect(() => {
    if (reservationPayload.isOnline) {
      setActiveCity("online");
      return;
    }

    if (!reservationPayload.branchIds.length || !branches.length) {
      setActiveCity(null);
      return;
    }

    const persistedBranch = branches.find((branch) => reservationPayload.branchIds.includes(branch.id));
    setActiveCity(persistedBranch?.city ?? null);
  }, [branches, reservationPayload.isOnline, reservationPayload.branchIds]);

  const {
    data: availability,
    isLoading,
    isFetching,
    isFetched,
    isPlaceholderData,
  } = useGetAvailabilityQuery(availabilityPayload, readyToFetch);

  const handleBranchChange = (value) => {
    const onlineOptionId = activeService?.options.find(
      (el) => el.category === RESERVATION_OPTIONS.LOCATION && el.publicVisible !== false,
    )?.id;
    setActiveTimeslot(null);
    setTimeSchedules([]);
    setAccumulatedAvailability([]);
    if (value === "online") {
      dispatchReservationPayload({ type: "setBranchOnline", onlineOptionId });
    } else {
      const b = branches.filter((el) => el.city === value);
      dispatchReservationPayload({ type: "setBranchOffline", onlineOptionId, branchIds: b.map((el) => el.id) });
    }

    setActiveCity(value);
    setAvailabilityAnchorDate(null);
  };

  const handleServiceChange = (value) => {
    const newService = services.find((el) => el.id === value);

    if (!newService) {
      console.error("Service not found");
      return;
    }

    // Detect child option via translation match before dispatching, so we can
    // mirror the legacy isChild side-effect (used by birthYear visibility).
    const childOptionInNewService = activeService
      ? reservationPayload.optionIds
          .map((optionId) => activeService.options.find((el) => el.id === optionId)?.translations[locale])
          .filter(Boolean)
          .map((translation) => newService.options.find((el) => el.translations[locale] === translation))
          .find((option) => ["Dziecko", "Dziecko / nastolatek"].includes(option?.name ?? ""))
      : undefined;
    const child = !!childOptionInNewService;

    if (child !== isChild) {
      setIsChild(child);
    }

    dispatchReservationPayload({
      type: "setService",
      serviceId: value,
      prevActiveService: activeService,
      newActiveService: newService,
      locale,
    });

    setActiveService(newService);
    setActiveTimeslot(null);
    setTimeSchedules([]);
    setAccumulatedAvailability([]);
    setAvailabilityAnchorDate(null);
  };

  const handleOptionChange = (value) => {
    const optionName = activeService?.options.find((el) => el.id === value)?.name;
    const child = ["Dziecko", "Dziecko / nastolatek"].includes(optionName ?? "");

    setIsChild(child);

    if (!child) {
      setBirthYear(null);
    }

    dispatchReservationPayload({ type: "setVisitType", optionId: value, activeService });

    // Use the functional updater so we don't clobber the visitTypeId/optionIds
    // just set by dispatchReservationPayload above (the closure-captured
    // reservationPayload is stale and would overwrite the reducer's update).
    setReservationPayload((prev) => ({
      ...prev,
      therapist: "",
      birthYear: child ? prev.birthYear : null,
    }));
    setActiveTimeslot(null);
    setTimeSchedules([]);
    setAccumulatedAvailability([]);
    setAvailabilityAnchorDate(null);
  };

  const handleNextClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(`/form${location.search}`);
  };

  const handleBirthYear = (value: string) => {
    setBirthYear(value);
    setReservationPayload({
      ...reservationPayload,
      birthYear: value,
    });
    setAccumulatedAvailability([]);
  };

  const handleLoadMore = () => {
    const newDays = loadedDays + 30;
    setLoadedDays(newDays);
    setAvailabilityPayload((prev) =>
      prev ? { ...prev, dateTo: dayjs().add(newDays, "days").format("YYYY-MM-DD") } : prev,
    );
  };

  useEffect(() => {
    if (readyToFetch && availability && availability.length === 0 && retries < MAX_AVAILABILITY_MONTH_RETRIES) {
      setRetries((prev) => prev + 1);
      handleLoadMore();
    }
  }, [availability, readyToFetch]);

  useEffect(() => {
    if (accumulatedAvailability && accumulatedAvailability.length > 0) {
      let therap: ISingleTherapist[] = [];

      therap = Array.from(
        new Map(
          accumulatedAvailability.map((el: ISingleAvailability) => [
            `${el.therapist.user.firstName} ${el.therapist.user.lastName}`,
            {
              id: el.therapist.id,
              name: `${el.therapist.user.firstName} ${el.therapist.user.lastName}`,
              lastName: el.therapist.user.lastName,
            },
          ]),
        ).values(),
      ) as ISingleTherapist[];
      setTherapists(therap);
    }
  }, [accumulatedAvailability]);

  useEffect(() => {
    if (readyToFetch && !isPlaceholderData && availability && availability.length > 0) {
      setAccumulatedAvailability((prev) => {
        const prevIds = new Set(prev.map((el) => `${el.scheduleId}_${el.date}_${el.startTime}`));
        const newItems = availability.filter((el) => !prevIds.has(`${el.scheduleId}_${el.date}_${el.startTime}`));
        return newItems.length > 0 ? [...prev, ...newItems] : prev;
      });
    }
  }, [availability, isPlaceholderData, readyToFetch]);

  useEffect(() => {
    if (
      (reservationPayload.serviceId || reservationPayload.isOnline || reservationPayload.branchIds.length > 0) &&
      !sentEvents.includes(TRACKING.OPEN)
    ) {
      setActiveStep(TRACKING.OPEN);
    }
  }, [
    reservationPayload.serviceId,
    reservationPayload.isOnline,
    reservationPayload.branchIds,
    sentEvents,
    setActiveStep,
  ]);

  useEffect(() => {
    if (isFetched) {
      setActiveStep(TRACKING.SEARCH);
    }
  }, [isFetched, setActiveStep]);

  useEffect(() => {
    if (activeService && locale === LOCALES.ENGLISH) {
      const lang = activeService.options.find(
        (el) => el.category === RESERVATION_OPTIONS.LANGUGAGE && el.publicVisible !== false,
      );
      if (lang) {
        dispatchReservationPayload({ type: "setLanguageOption", optionId: lang.id, enabled: true });
      }
    }
  }, [activeService, locale, dispatchReservationPayload]);

  useEffect(() => {
    if (showSelect && timeSchedules.length === 1) {
      setShowSelect(false);
    }
  }, [timeSchedules, showSelect]);

  useEffect(() => {
    const hasRequiredSelection =
      reservationPayload.visitTypeId && reservationPayload.serviceId && (activeCity || reservationPayload.isOnline);

    if (!hasRequiredSelection || (isChild && !birthYear)) {
      console.log("Form is incomplete, not ready to fetch availability.");
      // Keep the last query key stable so patient-type changes cannot disturb
      // unrelated form state. Fetching and display are gated separately.
      setReadyToFetch(false);
      return;
    }

    const payload: getAvailabilityParam = {
      customerId,
      serviceIds: [reservationPayload.serviceId],
      branchIds: reservationPayload.branchIds,
      optionIds: uniqueOptionIds(reservationPayload.optionIds),
      childBirthYear: isChild ? birthYear : null,
      dateFrom: dayjs().format("YYYY-MM-DD"),
      dateTo: dayjs().add(30, "days").format("YYYY-MM-DD"),
      includeOnline: reservationPayload.isOnline,
    };

    setAvailabilityPayload(payload);
    setReadyToFetch(true);
  }, [reservationPayload, isChild, birthYear, activeCity, customerId]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const branchName = queryParams.get("branch");
    const activeServiceId = queryParams.get("service");

    if (services && activeServiceId) {
      const service = services.find((el) => el.id === activeServiceId);
      if (service) {
        handleServiceChange(activeServiceId);
      }
    }

    if (branches && branchName) {
      const b = branches.filter((el) => el.city.toLowerCase() === branchName.toLowerCase());

      if (b.length > 0) {
        setActiveCity(b[0].city);
        setReservationPayload({
          ...reservationPayload,
          branchIds: b.map((el) => el.id),
        });
      }
    }
  }, [branches, services, location.search]);

  useEffect(() => {
    setAvailabilityAnchorDate(null);
  }, [reservationPayload.therapist]);

  useEffect(() => {
    setAccumulatedAvailability([]);
    setTimeSchedules([]);
    setActiveTimeslot(null);
    setAvailabilityAnchorDate(null);
    setReservationPayload((prev) => ({ ...prev, therapist: "" }));
  }, [reservationPayload.optionIds]);

  const branch = activeTimeslot ? getBranchById(activeTimeslot.branchId) : null;
  const childAge = 2024 - parseInt(birthYear || "2024");
  const summaryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeTimeslot && summaryRef.current) {
      summaryRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [activeTimeslot]);

  const [pendingPaymentLink, setPendingPaymentLink] = useState<string | null>(null);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const paymentLinkParam = queryParams.get("paymentLink");

    if (!paymentLinkParam) {
      setPendingPaymentLink(null);
      return;
    }

    const paymentLink = decodeURIComponent(paymentLinkParam);

    let paymentId = "";
    try {
      const parsedUrl = new URL(paymentLink);
      const paymentParam = parsedUrl.searchParams.get("id") || "";
      paymentId = paymentParam.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
    } catch {
      setPendingPaymentLink(null);
      return;
    }

    if (!paymentId) {
      setPendingPaymentLink(null);
      return;
    }

    fetch(`${API_URL}/public/payments/${paymentId}`)
      .then((res) => res.json())
      .then((data) => {
        if (data?.success === true) {
          setPendingPaymentLink(paymentLink);
        } else {
          setPendingPaymentLink(null);
        }
      })
      .catch(() => {
        setPendingPaymentLink(null);
      });
  }, [location.search]);

  if (pendingPaymentLink) {
    return (
      <ContinueTransaction
        url={pendingPaymentLink}
        onDiscard={() => {
          const queryParams = new URLSearchParams(location.search);
          queryParams.delete("paymentLink");
          navigate(
            {
              pathname: location.pathname,
              search: queryParams.toString() ? `?${queryParams.toString()}` : "",
            },
            { replace: true },
          );
          setPendingPaymentLink(null);
        }}
      />
    );
  }
  return (
    <>
      {services?.length && branches?.length ? (
        <>
          <StartContent
            activeCity={activeCity}
            cities={Array.from(new Set(branches.map((el) => el.city)))}
            handleBranchChange={handleBranchChange}
            handleServiceChange={handleServiceChange}
            handleOptionChange={handleOptionChange}
            activeService={activeService}
            services={services}
            isChild={isChild}
            birthYear={birthYear}
            setBirthYear={handleBirthYear}
            availability={readyToFetch ? availability : undefined}
            therapists={therapists}
          />
          {readyToFetch &&
          isFetched &&
          (availability.length > 0 || retries === MAX_AVAILABILITY_MONTH_RETRIES) &&
          reservationPayload.visitTypeId ? (
            <>
              {accumulatedAvailability && accumulatedAvailability.length > 0 ? (
                <div style={{ padding: 20 }}>
                  <SummaryTitle>{formatMessage({ defaultMessage: "Dostępne terminy", id: "hpEV8w" })}</SummaryTitle>
                  <AvailabilitySlots
                    duration={activeService.bookingTime}
                    availability={
                      reservationPayload.therapist
                        ? accumulatedAvailability.filter(
                            (el) =>
                              `${el.therapist.user.firstName} ${el.therapist.user.lastName}` ===
                              reservationPayload.therapist,
                          )
                        : accumulatedAvailability
                    }
                    loadedDays={loadedDays}
                    setTimeSchedules={setTimeSchedules}
                    onLoadMore={handleLoadMore}
                    isLoadingMore={isFetching}
                    persistedAnchorDate={availabilityAnchorDate}
                    onAnchorDateChange={setAvailabilityAnchorDate}
                  />
                  {activeTimeslot && (
                    <div ref={summaryRef} style={{ marginTop: 20 }}>
                      <SummaryTitle>{formatMessage({ defaultMessage: "Twoja wizyta u:", id: "BGY/Ks" })}</SummaryTitle>

                      <Flex vertical>
                        <div>
                          {showSelect ? (
                            <Select
                              style={{ width: "100%" }}
                              defaultValue={activeTimeslot.scheduleId}
                              options={timeSchedules
                                .sort((a, b) => {
                                  const nameA = a.therapist.user.lastName.toLowerCase();
                                  const nameB = b.therapist.user.lastName.toLowerCase();
                                  return nameA.localeCompare(nameB);
                                })
                                .map((el) => ({
                                  label: (
                                    <div>{`${el.therapist.user.firstName} ${el.therapist.user.lastName} ${el.therapist.user.title ? `(${el.therapist.user.title})` : ""}`}</div>
                                  ),
                                  value: el.scheduleId,
                                }))}
                              onChange={(value) => {
                                const selected = timeSchedules.find((el) => el.scheduleId === value);

                                setActiveTimeslot(selected);
                                setShowSelect(false);
                              }}
                            />
                          ) : (
                            <Flex>
                              {activeTimeslot.therapist.user.avatarUrl && (
                                <Avatar
                                  src={activeTimeslot.therapist.user.avatarUrl}
                                  style={{ marginRight: 10 }}
                                  size={90}
                                />
                              )}

                              <Flex
                                vertical
                                style={{
                                  width: activeTimeslot.therapist.user.avatarUrl ? "calc(100% - 90px)" : "auto",
                                }}
                              >
                                <Typography.Text strong>
                                  {activeTimeslot.therapist.user.firstName} {activeTimeslot.therapist.user.lastName}{" "}
                                  {timeSchedules.length > 1 && (
                                    <Button
                                      type="text"
                                      onClick={() => setShowSelect(true)}
                                      style={{ textDecoration: "underline", textTransform: "uppercase" }}
                                    >
                                      {formatMessage({ defaultMessage: "Zmień", id: "OH4sDW" })}
                                    </Button>
                                  )}
                                </Typography.Text>
                                {activeTimeslot.therapist.user.title && (
                                  <Typography.Text type="secondary" italic>
                                    {activeTimeslot.therapist.user.title}
                                  </Typography.Text>
                                )}
                                {activeTimeslot.therapist.user.bio && (
                                  <Typography.Text style={{ marginBottom: 20 }}>
                                    {activeTimeslot.therapist.user.bio}
                                  </Typography.Text>
                                )}
                              </Flex>
                            </Flex>
                          )}
                        </div>
                      </Flex>

                      <div>
                        <Typography.Text style={{ margin: "10px 10px 0 0", display: "inline-block", minWidth: 110 }}>
                          {formatMessage({ defaultMessage: "Data", id: "XHHR08" })}:{" "}
                        </Typography.Text>
                        <Typography.Text strong>
                          {dayjs(`${activeTimeslot.date} ${activeTimeslot.startTime}`, "YYYY-MM-DD HH:mm:ss").format(
                            "DD MMMM YYYY, HH:mm",
                          )}
                        </Typography.Text>
                      </div>
                      <div>
                        <Typography.Text style={{ marginRight: "10px", display: "inline-block", minWidth: 110 }}>
                          {formatMessage({ defaultMessage: "Koszt wizyty", id: "qfbfoZ" })}:
                        </Typography.Text>

                        <Typography.Text strong>
                          {`${price} 
                          ${currency}`}
                        </Typography.Text>
                      </div>
                      <div>
                        <Typography.Text style={{ marginRight: 10, display: "inline-block", minWidth: 110 }}>
                          {formatMessage({ defaultMessage: "Adres gabinetu", id: "IvLSQl" })}:
                        </Typography.Text>
                        <Typography.Text strong>
                          {reservationPayload.isOnline
                            ? formatMessage({ defaultMessage: "Wizyta online", id: "iysARY" })
                            : `${branch.street}, ${branch.city}`}
                        </Typography.Text>
                      </div>
                      {isChild && childAge <= activeTimeslot.therapist.noChildOnFirstVisitAge && (
                        <div style={{ marginTop: 20, fontStyle: "italic" }}>
                          <Typography.Text>
                            {formatMessage({
                              defaultMessage:
                                "Uwaga: Na pierwszą wizytę zapraszamy jednego lub oboje rodziców/opiekunów",
                              id: "TJusWe",
                            })}
                          </Typography.Text>{" "}
                          <Typography.Text strong>
                            {formatMessage({
                              defaultMessage: "BEZ dziecka",
                              id: "Pj/7gf",
                            })}
                            .
                          </Typography.Text>
                        </div>
                      )}

                      <Flex justify="flex-end" style={{ width: "100%", marginTop: 20 }}>
                        <Button type="primary" onClick={handleNextClick} style={{ boxShadow: "none" }}>
                          {formatMessage({ defaultMessage: "Dalej", id: "+N4HKT" })}
                        </Button>
                      </Flex>
                    </div>
                  )}
                </div>
              ) : (
                <StyledNotification vertical align="center" style={{ marginTop: 20 }}>
                  <Typography.Text>
                    {formatMessage({ defaultMessage: "Brak dostępnych terminów", id: "EDBKe2" })}
                  </Typography.Text>
                  <Typography.Text>
                    {formatMessage({
                      defaultMessage: "Spróbuj znaleźć termin w innej lokalizacji lub online",
                      id: "don3dF",
                    })}
                  </Typography.Text>
                </StyledNotification>
              )}
            </>
          ) : (
            <>
              {readyToFetch &&
                (isLoading ||
                  isPlaceholderData ||
                  (availability &&
                    availability.length === 0 &&
                    retries > 0 &&
                    retries < MAX_AVAILABILITY_MONTH_RETRIES)) && <Loader />}
            </>
          )}
        </>
      ) : (
        <Loader />
      )}
    </>
  );
};
